// Loads GET /api/diNarrativeStandards/{period}/{contIndex} (see api/diNarrativeStandards).
// Shared by the manager modal and the summary tile. Refetches update `view` in place — only the
// first load (no view yet) reports `initialLoading`, so a refresh never blanks the UI.

import { useCallback, useEffect, useRef, useState } from 'react';

async function fetchView(period, contIndex) {
  const res = await fetch(
    `/api/diNarrativeStandards/${encodeURIComponent(period)}/${encodeURIComponent(contIndex)}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try { msg = JSON.parse(text).error || text; } catch { /* plain text */ }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function useNarrativeView({ period, contIndex, enabled = true, reloadToken = 0 }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const key = `${period}|${contIndex}`;
  const keyRef = useRef(key);

  const load = useCallback(async () => {
    if (!enabled || period == null || contIndex == null) return null;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const v = await fetchView(period, contIndex);
      if (mine !== seq.current) return null;     // a newer request superseded this one
      setView(v);
      setError(null);
      return v;
    } catch (e) {
      if (mine === seq.current) setError(e);
      return null;
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [enabled, period, contIndex]);

  useEffect(() => {
    if (keyRef.current !== key) { keyRef.current = key; setView(null); setError(null); }   // different client
    if (!enabled) setLoading(false);
    load();
    return () => { seq.current += 1; };
  }, [load, enabled, key, reloadToken]);

  // `setView` lets the manager modal patch the list optimistically (and put it back if a save is refused).
  return { view, setView, error, loading, initialLoading: loading && !view, refetch: load };
}
